import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, XCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  useContextOperator,
  useDocTemplateCheck,
  useDocTemplates,
  useGeneratedDocs,
  useGenerateDocument,
  useTaskPolling,
} from "../../hooks";
import { operatorShort, pickTemplatesForOperator } from "../../operatorTemplates";
import { TemplateFieldsPanel } from "../TemplateFieldsPanel";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { DOC_TEMPLATE_KIND_LABELS, type DocTemplateKind, type OfficialApplicationDetail } from "../../types";
import { InfoHint } from "./InfoHint";

const KIND = "official_application";

function checkErrorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { errors?: { detail?: unknown } } } })
    ?.response?.data?.errors?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const first = Object.values(detail as Record<string, unknown>).flat()[0];
    if (typeof first === "string") return first;
  }
  return "Nie udało się sprawdzić szablonu w tym kontekście.";
}

/**
 * Generacja pisma wniosku z szablonu ODT (silnik #zmiennych#): wybór szablonu
 * rodzaju procedury → sprawdzenie, czy każda #zmienna# ma wartość → nadpisania
 * pól → generacja w tle → pobranie. Klon sprawdzonego przepływu zgód/wniosku
 * regulacyjnego; szablony podmienia się w „Generacji dokumentów” bez kodu.
 */
export function GenerateApplicationDocModal({
  app, onClose,
}: {
  app: OfficialApplicationDetail;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: templates, isLoading: templatesLoading } = useDocTemplates();
  const wantedKind = app.config?.doc_template_kind ?? "";
  // Serwer może zaproponować dedykowany wariant druku dla TEGO wniosku — dziś:
  // lokalizacyjny do GDDKiA → druk „zezwolenie na lokalizację w pasie drogowym".
  const proposedKind = app.proposed_doc_template_kind || wantedKind;
  // Pakiet WPW: obok „karty informacyjnej" (app_wpw) faza dokumentu obsługuje też
  // formularze ENERGA statusowe (projektowane/likwidowane/modernizowane) — pokazujemy
  // TYLKO te statusy, które faktycznie są na mapie (DXF, 1–3), z serwera.
  const allowedKinds =
    app.application_type === "wpw_pakiet"
      ? [wantedKind, ...(app.wpw_form_kinds ?? []), "general"]
      : [...new Set([proposedKind, wantedKind, "general"])];
  // Czytelna polska nazwa rodzaju zamiast surowego sluga (np. „Pakiet WPW — karta
  // informacyjna" zamiast „app_wpw").
  const wantedKindLabel =
    DOC_TEMPLATE_KIND_LABELS[wantedKind as DocTemplateKind] ?? wantedKind;
  // Operator (OSD) projektu wniosku — pod niego dobieramy „niewspólne" szablony.
  const { data: operatorInfo } = useContextOperator(KIND, app.id);
  const operator = operatorInfo?.operator ?? "energa";
  const options = pickTemplatesForOperator(templates ?? [], allowedKinds, operator);
  // Wniosek uniwersalny: szablon wskazano już przy zakładaniu — to ON jest typem tego
  // pisma, więc wchodzi tu wybrany (zmienić i tak można, lista zostaje pełna).
  const [templateId, setTemplateId] = useState(app.doc_template ?? "");
  // Auto-propozycja: gdy serwer wskazuje dedykowany wariant (GDDKiA), wybieramy
  // jego najlepszy szablon od razu — RAZ na otwarcie modala, wybór można zmienić.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || templatesLoading || proposedKind === wantedKind) return;
    const proposal = options.find((o) => o.template.kind === proposedKind);
    if (proposal) {
      preselected.current = true;
      setTemplateId(proposal.template.id);
    }
  }, [templatesLoading, options, proposedKind, wantedKind]);
  const selectedOption = options.find((o) => o.template.id === templateId);
  const fallbackWarning =
    selectedOption && !selectedOption.exact && !selectedOption.shared;
  const check = useDocTemplateCheck(templateId, KIND, app.id);
  const generate = useGenerateDocument();
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [emptyMode, setEmptyMode] = useState<"blank" | "marker">("blank");
  // pola, których ręczne wartości zapiszą się też NA STAŁE do bazy (opt-in per pole)
  const [persist, setPersist] = useState<string[]>([]);
  const setOverride = (name: string, value: string) =>
    setOverrides((s) => ({ ...s, [name]: value }));
  const setPersistFor = (name: string, on: boolean) =>
    setPersist((s) => (on ? [...new Set([...s, name])] : s.filter((n) => n !== name)));
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const running = generate.isPending
    || (!!taskId && !["success", "failure", "cancelled"].includes(taskStatus ?? ""));

  const { data: lastDocs } = useGeneratedDocs(KIND, [app.id]);
  const last = lastDocs?.[0];
  // Po wygenerowaniu kreator otwiera edytor na pełnym ekranie: pola ręczne `#wpisz_…#`
  // szablonu uzupełnia się bez pobierania pliku (auto, gdy szablon takie pola ma).
  const { data: editorAvailability } = useEditorAvailability();
  const editorAvailable = !!editorAvailability?.available;
  const [editorDocId, setEditorDocId] = useState<string | null>(null);
  const autoOpened = useRef<string | null>(null);
  const manualFields = selectedOption?.template.manual_fields ?? [];
  const lastIsEditable = !!last?.file && /\.(odt|ott|fodt|docx|doc|rtf)$/i.test(last.file.original_filename);

  useEffect(() => {
    if (taskId && taskStatus && ["success", "failure", "cancelled"].includes(taskStatus)) {
      qc.invalidateQueries({ queryKey: ["pp", "generated-docs"] });
      qc.invalidateQueries({ queryKey: ["pp", "application", app.id] });
    }
  }, [taskId, taskStatus, qc, app.id]);

  const start = () => {
    if (!templateId || !check.data?.ok) return;
    const cleanOverrides = Object.fromEntries(
      Object.entries(overrides).filter(([, v]) => v.trim() !== ""),
    );
    generate.mutate(
      {
        templateId, contextKind: KIND, contextId: app.id, overrides: cleanOverrides, emptyMode,
        persist: persist.filter((n) => (overrides[n] ?? "").trim() !== ""),
      },
      { onSuccess: (r) => setTaskId(r.task_id) },
    );
  };

  const done = taskStatus === "success";
  const failed = taskStatus === "failure" || generate.isError;

  useEffect(() => {
    if (!done || !editorAvailable || !last?.id || last.status !== "success" || !lastIsEditable) return;
    if (manualFields.length === 0 || autoOpened.current === last.id) return;
    autoOpened.current = last.id;
    setEditorDocId(last.id);
  }, [done, editorAvailable, last, lastIsEditable, manualFields.length]);

  return (
    <Modal open onClose={onClose} title={`Generuj pismo — ${app.number}`} size="xl">
      <div className="space-y-3">
        <label className="block">
          <span className="section-label flex items-center gap-1">
            Szablon dokumentu *
            <InfoHint text={`${
              wantedKind === "general"
                // Wniosek uniwersalny: rodzajem JEST „Uniwersalny", więc zdanie
                // „szablony rodzaju Uniwersalny + uniwersalne" nic by nie znaczyło.
                ? "Lista pokazuje wszystkie szablony rodzaju „Uniwersalny”. Wybrany jest ten wskazany przy zakładaniu wniosku — możesz go zmienić, ale to on mówi, czym jest to pismo (jego nazwa stoi przy wniosku na liście)."
                : `Lista pokazuje szablony rodzaju „${wantedKindLabel}” + uniwersalne.`
            } Szablon to plik ODT ze #zmiennymi# — pełną tabelę zmiennych (co skąd się liczy) znajdziesz w Generacji dokumentów. Dane urzędu, działek, osoby odpowiedzialnej i pełnomocnictw wchodzą automatycznie z faz pakietu.`} />
          </span>
          {templatesLoading ? (
            <div className="py-2"><Spinner className="h-4 w-4" /></div>
          ) : (
            <select
              className="input-base mt-1"
              value={templateId}
              disabled={running}
              onChange={(e) => { setTemplateId(e.target.value); setTaskId(null); }}
            >
              <option value="">— wybierz szablon —</option>
              {options.map((o) => (
                <option key={o.template.id} value={o.template.id}>
                  {o.template.name} {o.badge}
                  {o.template.id === app.doc_template ? " · wskazany przy zakładaniu" : ""}
                  {proposedKind !== wantedKind && o.template.kind === proposedKind
                    ? " · proponowany dla tego adresata"
                    : ""}
                </option>
              ))}
            </select>
          )}
          {!templatesLoading && options.length === 0 && (
            <p className="text-xs text-amber-700 mt-1">
              Brak szablonu rodzaju „{wantedKindLabel}” — wgraj go w Generacji dokumentów
              (możesz na razie użyć roboczego; podmienisz bez zmian w systemie).
            </p>
          )}
          {fallbackWarning && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
              Brak szablonu dla operatora {operatorShort(operator)} — użyto szablonu innego
              operatora. Wgraj wariant {operatorShort(operator)} lub oznacz szablon jako uniwersalny.
            </p>
          )}
        </label>

        {templateId && check.isLoading && (
          <p className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <Spinner className="h-3.5 w-3.5" /> Sprawdzanie zmiennych szablonu w tym kontekście…
          </p>
        )}
        {templateId && check.isError && (
          <p className="text-xs text-red-600 inline-flex items-center gap-1">
            <XCircle size={14} /> {checkErrorMessage(check.error)}
          </p>
        )}
        {templateId && check.data && (
          <TemplateFieldsPanel
            fields={check.data.fields}
            overrides={overrides}
            onOverride={setOverride}
            emptyMode={emptyMode}
            onEmptyMode={setEmptyMode}
            persist={persist}
            onPersist={setPersistFor}
            contextKind={KIND}
            contextId={app.id}
            branchSetup={check.data.client_branch_setup}
          />
        )}

        {/* items-stretch: przycisk główny nie ma obramowania i jest o 2 px niższy od
            drugorzędnych i linków — rozciągnięcie w wierszu wyrównuje wszystkie do jednej
            wysokości; teksty stanu centrują się same (self-center). */}
        <div className="flex flex-wrap items-stretch gap-3">
          <Button onClick={start} loading={running} disabled={!templateId || !check.data?.ok}>
            Generuj
          </Button>
          {/* The disabled button always says WHY it cannot be clicked. */}
          {!templateId && (
            <span className="self-center text-xs text-[var(--color-text-muted)]">Najpierw wybierz szablon.</span>
          )}
          {templateId && check.data && !check.data.ok && (
            <span className="self-center text-xs text-red-600">
              Zablokowane — {check.data.missing.length}{" "}
              {check.data.missing.length === 1 ? "zmienna niedostępna" : "zmiennych niedostępnych"} w tym
              kontekście (czerwona lista wyżej).
            </span>
          )}
          {done && <span className="self-center text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> Wygenerowano</span>}
          {failed && <span className="self-center text-xs text-red-600 inline-flex items-center gap-1"><XCircle size={14} /> Błąd generacji</span>}
          {last?.id && last.status === "success" && lastIsEditable && editorAvailable && (
            <Button
              variant="secondary"
              disabled={running}
              onClick={() => setEditorDocId(last.id)}
              title="Otwórz wygenerowane pismo w edytorze na pełnym ekranie — uzupełnij pola ręczne bez pobierania pliku"
            >
              Uzupełnij w edytorze
            </Button>
          )}
          {last?.pdf_file?.url && (
            <a
              href={last.pdf_file.url}
              target="_blank"
              rel="noreferrer"
              title="PDF wygenerowanej wersji — do kontroli"
              className="inline-flex items-center gap-1 text-xs font-mono border border-[var(--color-border-strong)] px-3 py-2.5 hover:bg-[var(--color-bg-elevated)]"
            >
              <Download size={12} /> {last.pdf_file.original_filename}
            </a>
          )}
          {last?.file?.url && (
            <a
              href={last.file.url}
              download={last.file.original_filename}
              title="Źródło (ODT)"
              className="inline-flex items-center gap-1 text-xs font-mono border border-[var(--color-border-strong)] px-3 py-2.5 hover:bg-[var(--color-bg-elevated)]"
            >
              <Download size={12} /> {last.pdf_file?.url ? "ODT" : last.file.original_filename}
            </a>
          )}
          {last?.status === "success" && !last.pdf_file && last.pdf_error && (
            <span className="self-center text-xs text-red-600">Brak PDF-a: {last.pdf_error}</span>
          )}
        </div>
        {manualFields.length > 0 && (
          <p className="text-[11px] text-[var(--color-text-muted)]">
            Szablon ma {manualFields.length === 1 ? "pole ręczne" : `pola ręczne (${manualFields.length})`}:{" "}
            {manualFields.map((n) => <span key={n} className="font-mono mr-1">#{n}#</span>)}
            — po wygenerowaniu edytor otworzy się na pełnym ekranie, żebyś je uzupełnił(a).
            {!editorAvailable && " Edytor w przeglądarce jest wyłączony na tym serwerze — pobierz ODT, uzupełnij i wgraj jako własny plik."}
          </p>
        )}
      </div>
      {editorDocId && (
        <OdtEditorScreen
          kind="generated"
          id={editorDocId}
          title={`Uzupełnij pismo — ${app.number}`}
          onClose={(saved) => {
            setEditorDocId(null);
            qc.invalidateQueries({ queryKey: ["pp", "generated-docs"] });
            qc.invalidateQueries({ queryKey: ["pp", "application", app.id] });
            if (saved) onClose();
          }}
        />
      )}
    </Modal>
  );
}
